import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SkeletonCard } from '../components/SkeletonLoader'
import { FiSave, FiBriefcase, FiMail, FiPhone, FiMapPin, FiFileText, FiInfo, FiExternalLink, FiHelpCircle, FiCreditCard, FiUsers, FiCheck, FiArrowRight, FiPlus, FiCalendar, FiRefreshCw } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../config/api'
import { configuracionGlobalAPI, billingAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { PageHeader } from '../ui'
import { formatearFecha } from '../utils/fechas'

const PLAN_INFO = {
  pro: { name: 'Pro', price: 1.50, annualPrice: 1.25, color: 'blue', features: ['Fichaje entrada/salida + pausas', 'Informes básicos', 'App móvil responsive', 'Cumplimiento RDL 8/2019'] },
  avanzado: { name: 'Avanzado', price: 2.95, annualPrice: 2.46, color: 'purple', features: ['Todo lo del plan Pro', 'Gestión de ausencias/vacaciones', 'Correcciones de fichajes', 'Informes avanzados + exportación', 'Geolocalización', 'Soporte prioritario'] }
}

const ConfiguracionEmpresa = () => {
  const { empleado, empresaInfo } = useAuth()
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [empresa, setEmpresa] = useState(null)
  const [tabActiva, setTabActiva] = useState(searchParams.get('tab') || 'empresa')
  const [formData, setFormData] = useState({ nombre: '', cif: '', direccion: '', telefono: '', email: '' })
  const [soporteUrl, setSoporteUrl] = useState('')
  const [billingStatus, setBillingStatus] = useState(null)
  const [loadingBilling, setLoadingBilling] = useState(false)
  const [slotsDelta, setSlotsDelta] = useState(1) // positivo = ampliar, negativo = reducir
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [showChangePlanModal, setShowChangePlanModal] = useState(false)
  const [changePlanTarget, setChangePlanTarget] = useState(null)
  const [changingPlan, setChangingPlan] = useState(false)
  const [selectedSlots, setSelectedSlots] = useState(5)
  const [selectedInterval, setSelectedInterval] = useState('monthly')

  const buildApiUrl = (endpoint) => {
    if (esGestion && empresaGestionId) {
      const separator = endpoint.includes('?') ? '&' : '?'
      return `${API_URL}${endpoint}${separator}empresa_id=${empresaGestionId}`
    }
    return `${API_URL}${endpoint}`
  }

  useEffect(() => {
    cargarEmpresa()
    if (empleado?.rol === 'admin') cargarSoporteUrl()
  }, [empresaGestionId])

  useEffect(() => {
    if (tabActiva === 'licencia' && empresa) cargarBillingStatus()
  }, [tabActiva, empresa])

  const cargarSoporteUrl = async () => {
    try {
      const response = await configuracionGlobalAPI.get('soporte_url')
      if (response.data?.valor) setSoporteUrl(response.data.valor)
    } catch (error) { console.error('Error cargando URL de soporte:', error) }
  }

  const cargarEmpresa = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl('/empresas/mi-empresa'), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setEmpresa(data)
        setFormData({ nombre: data.nombre || '', cif: data.cif || '', direccion: data.direccion || '', telefono: data.telefono || '', email: data.email || '' })
      }
    } catch (error) { toast.error('Error al cargar datos de la empresa') }
    finally { setLoading(false) }
  }

  const cargarBillingStatus = async () => {
    setLoadingBilling(true)
    try {
      if (esGestion && empresa) {
        setBillingStatus({
          plan: empresa.plan,
          subscriptionStatus: empresa.subscription_status,
          billingInterval: empresa.billing_interval,
          trialEndsAt: empresa.trial_ends_at,
          slotsTotal: empresa.slots_total || 0,
          slotsUsados: empresa.empleados_activos || empresa.total_empleados || 0,
          fechaExpiracion: empresa.fecha_expiracion,
          currentPeriodEnd: empresa.fecha_expiracion,
        })
      } else {
        const res = await billingAPI.getStatus()
        setBillingStatus(res.data)
      }
    } catch (error) { console.error('Error cargando estado de facturación:', error) }
    finally { setLoadingBilling(false) }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.nombre) { toast.error('El nombre de la empresa es obligatorio'); return }
    setGuardando(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl('/empresas/mi-empresa'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Error al actualizar empresa')
      toast.success('Datos actualizados')
      setEmpresa(data)
    } catch (error) { toast.error(error.message) }
    finally { setGuardando(false) }
  }

  const handleSlotsChange = async () => {
    if (slotsDelta === 0) return
    setSlotsLoading(true)
    try {
      if (slotsDelta > 0) {
        const res = await billingAPI.addSlots({ additional_slots: slotsDelta })
        toast.success(res.data.message)
      } else {
        const newTotal = (billingStatus?.slotsTotal || 0) + slotsDelta
        if (!window.confirm(`¿Reducir a ${newTotal} plazas? Se mantendrán las actuales hasta el próximo cobro.`)) {
          setSlotsLoading(false)
          return
        }
        const res = await billingAPI.reduceSlots({ new_slots: newTotal })
        toast.success(res.data.message)
      }
      setSlotsDelta(1)
      cargarBillingStatus()
    } catch (err) { toast.error(err.response?.data?.error || 'Error al modificar plazas') }
    finally { setSlotsLoading(false) }
  }

  const handleChangePlan = async () => {
    if (!changePlanTarget) return
    setChangingPlan(true)
    try {
      const res = await billingAPI.changePlan({ new_plan: changePlanTarget.plan, new_interval: changePlanTarget.interval })
      toast.success(res.data.message)
      setShowChangePlanModal(false)
      setChangePlanTarget(null)
      cargarBillingStatus()
    } catch (err) { toast.error(err.response?.data?.error || 'Error al cambiar el plan') }
    finally { setChangingPlan(false) }
  }

  if (loading) return <SkeletonCard count={2} />

  if (!empresa) {
    return (
      <div className="ui-card p-5 text-center py-12">
        <FiBriefcase className="mx-auto h-12 w-12 text-[var(--ink-4)]" />
        <h3 className="mt-3 text-lg font-medium text-[var(--ink)]">No se pudo cargar la información</h3>
      </div>
    )
  }

  const infoSource = esGestion ? empresa : (empresaInfo || empresa)
  const currentPlan = billingStatus?.plan || infoSource?.plan || 'pro'
  const subscriptionStatus = billingStatus?.subscriptionStatus || infoSource?.subscription_status
  const isTrialing = subscriptionStatus === 'trialing'
  const isActive = subscriptionStatus === 'active'
  const isInterno = subscriptionStatus === 'interno'
  const billingInterval = billingStatus?.billingInterval || infoSource?.billing_interval || 'monthly'

  // Badge de estado
  const StatusBadge = () => {
    if (isActive) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-700"><FiCheck className="h-3 w-3" />Activo</span>
    if (isInterno) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-indigo-100 text-indigo-700"><FiCheck className="h-3 w-3" />Interno</span>
    if (isTrialing) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-700">Trial</span>
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-700">Expirado</span>
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ C1"
        kicker="Datos de la empresa"
        title="Información"
        italic="general."
        sub="Información fiscal, plan contratado y consumo de licencias."
        actions={
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-pill px-3 py-1.5">
              <span className="text-[var(--ink-3)]">Plan</span>
              <span className="font-medium text-[var(--ink)]">{PLAN_INFO[currentPlan]?.name || currentPlan}</span>
              <StatusBadge />
            </div>
            {(isActive || isTrialing) && (
              <div className="flex items-center gap-1.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-pill px-3 py-1.5">
                <FiUsers className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                <span className="font-medium text-[var(--ink)] tnum">
                  {billingStatus?.slotsUsados ?? empresa.empleados_activos ?? 0}
                  {billingStatus?.slotsTotal ? <span className="text-[var(--ink-3)] font-normal">/{billingStatus.slotsTotal}</span> : ''}
                </span>
              </div>
            )}
          </div>
        }
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-[var(--line)]">
        {[
          { id: 'empresa', label: 'Información', icon: FiBriefcase },
          { id: 'licencia', label: 'Licencia', icon: FiCreditCard },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setTabActiva(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 font-medium text-sm border-b-2 transition-colors ${
              tabActiva === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-[var(--ink-3)] hover:text-[var(--ink-2)]'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════ */}
      {/* Tab: Información de la empresa                  */}
      {/* ════════════════════════════════════════════════ */}
      {tabActiva === 'empresa' && (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ── Identificación ── */}
          <div className="ui-card p-5 p-0 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-5 py-3 border-b border-blue-100/50">
              <h3 className="text-sm font-semibold text-[var(--ink)] flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center">
                  <FiBriefcase className="h-3.5 w-3.5 text-blue-600" />
                </div>
                Identificación
              </h3>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-4">
                <label className="text-xs font-medium text-[var(--ink-3)] mb-1.5 block">Razón social *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiBriefcase className="h-4 w-4 text-[var(--ink-4)]" />
                  </div>
                  <input type="text" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] pl-10" required placeholder="Nombre completo de la empresa" />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-[var(--ink-3)] mb-1.5 block">CIF / NIF</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiFileText className="h-4 w-4 text-[var(--ink-4)]" />
                  </div>
                  <input type="text" value={formData.cif} onChange={(e) => setFormData({ ...formData, cif: e.target.value })}
                    className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] pl-10" placeholder="B12345678" />
                </div>
              </div>
            </div>
          </div>

          {/* ── Contacto ── */}
          <div className="ui-card p-5 p-0 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-5 py-3 border-b border-green-100/50">
              <h3 className="text-sm font-semibold text-[var(--ink)] flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-green-100 flex items-center justify-center">
                  <FiPhone className="h-3.5 w-3.5 text-green-600" />
                </div>
                Contacto
              </h3>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-[var(--ink-3)] mb-1.5 block">Teléfono</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiPhone className="h-4 w-4 text-[var(--ink-4)]" />
                  </div>
                  <input type="tel" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] pl-10" placeholder="+34 912 345 678" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[var(--ink-3)] mb-1.5 block">Email de contacto</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiMail className="h-4 w-4 text-[var(--ink-4)]" />
                  </div>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] pl-10" placeholder="contacto@empresa.com" />
                </div>
              </div>
            </div>
          </div>

          {/* ── Dirección ── */}
          <div className="ui-card p-5 p-0 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-3 border-b border-amber-100/50">
              <h3 className="text-sm font-semibold text-[var(--ink)] flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center">
                  <FiMapPin className="h-3.5 w-3.5 text-amber-600" />
                </div>
                Dirección fiscal
              </h3>
            </div>
            <div className="p-5">
              <div className="relative">
                <div className="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                  <FiMapPin className="h-4 w-4 text-[var(--ink-4)]" />
                </div>
                <textarea value={formData.direccion} onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  rows={2} className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] pl-10 resize-none" placeholder="Calle, número, código postal, ciudad, provincia" />
              </div>
            </div>
          </div>

          {/* ── Footer: registro + guardar ── */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-[var(--ink-4)]">
              <FiCalendar className="h-3.5 w-3.5" />
              Registrada el {formatearFecha(empresa.created_at)}
            </div>
            <button type="submit" disabled={guardando} className="btn btn-primary btn-ripple">
              <FiSave className="mr-2 h-4 w-4" />{guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>

          {/* ── Soporte ── */}
          {empleado?.rol === 'admin' && soporteUrl && (
            <div className="ui-card p-5 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <FiHelpCircle className="h-4 w-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[var(--ink)]">Soporte técnico</p>
                  <p className="text-xs text-[var(--ink-4)]">Abre un ticket para reportar incidencias</p>
                </div>
              </div>
              <a href={soporteUrl} target="_blank" rel="noopener noreferrer"
                className="btn btn-secondary text-sm flex items-center gap-1.5">
                <FiExternalLink className="h-3.5 w-3.5" /> Abrir soporte
              </a>
            </div>
          )}
        </form>
      )}

      {/* ════════════════════════════════════════════════ */}
      {/* Tab: Licencia                                   */}
      {/* ════════════════════════════════════════════════ */}
      {tabActiva === 'licencia' && (() => {
        const slotsTotal = billingStatus?.slotsTotal || infoSource?.slots_total || 0
        const slotsUsados = billingStatus?.slotsUsados ?? 0
        const trialEnd = (billingStatus?.trialEndsAt || infoSource?.trial_ends_at) ? new Date(billingStatus?.trialEndsAt || infoSource?.trial_ends_at) : null
        const diasRestantes = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24))) : 0
        const nextBilling = billingStatus?.currentPeriodEnd ? new Date(billingStatus.currentPeriodEnd) : (empresa.fecha_expiracion ? new Date(empresa.fecha_expiracion) : null)

        return (
          <div className="space-y-5">
            {loadingBilling && !billingStatus ? (
              <SkeletonCard count={1} />
            ) : (
              <>
                {/* ── Resumen ── */}
                <div className="ui-card p-5 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-[var(--ink-2)] uppercase tracking-wide">Resumen de licencia</h3>
                    <button onClick={cargarBillingStatus} className="text-[var(--ink-4)] hover:text-blue-600 transition-colors" title="Actualizar">
                      <FiRefreshCw className={`h-4 w-4 ${loadingBilling ? 'animate-spin' : ''}`} />
                    </button>
                  </div>

                  {/* Fila principal: métricas clave en línea */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Plan */}
                    <div className="bg-[var(--bg-2)] rounded-lg px-3 py-2.5">
                      <p className="text-[11px] text-[var(--ink-4)] font-medium uppercase">Plan</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-base font-bold text-[var(--ink)]">{PLAN_INFO[currentPlan]?.name || currentPlan}</span>
                        <StatusBadge />
                      </div>
                    </div>

                    {/* Modalidad */}
                    <div className="bg-[var(--bg-2)] rounded-lg px-3 py-2.5">
                      <p className="text-[11px] text-[var(--ink-4)] font-medium uppercase">Facturación</p>
                      {isInterno ? (
                        <p className="text-base font-bold text-indigo-700 mt-0.5">Gestión interna</p>
                      ) : isActive ? (
                        <>
                          <p className="text-base font-bold text-[var(--ink)] mt-0.5">
                            {billingInterval === 'yearly' ? 'Anual' : 'Mensual'}
                          </p>
                          {billingInterval === 'yearly' && (
                            <p className="text-[10px] text-green-600 font-medium">Ahorro 17% aplicado</p>
                          )}
                        </>
                      ) : (
                        <p className="text-base font-bold text-[var(--ink-4)] mt-0.5">—</p>
                      )}
                    </div>

                    {/* Plazas */}
                    <div className="bg-[var(--bg-2)] rounded-lg px-3 py-2.5">
                      <p className="text-[11px] text-[var(--ink-4)] font-medium uppercase">Plazas</p>
                      {isTrialing ? (
                        <p className="text-base font-bold text-[var(--ink)] mt-0.5">Ilimitadas</p>
                      ) : slotsTotal > 0 ? (
                        <div className="mt-0.5">
                          <div className="flex items-baseline gap-1">
                            <span className="text-base font-bold text-[var(--ink)]">{slotsUsados}</span>
                            <span className="text-sm text-[var(--ink-4)]">/ {slotsTotal}</span>
                          </div>
                          <div className="mt-1 w-full bg-gray-200 rounded-full h-1">
                            <div
                              className={`h-1 rounded-full ${slotsUsados >= slotsTotal ? 'bg-red-500' : slotsUsados >= slotsTotal * 0.8 ? 'bg-amber-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(100, (slotsUsados / slotsTotal) * 100)}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <p className="text-base font-bold text-[var(--ink-4)] mt-0.5">—</p>
                      )}
                    </div>

                    {/* Próximo cobro / fin trial */}
                    <div className="bg-[var(--bg-2)] rounded-lg px-3 py-2.5">
                      <p className="text-[11px] text-[var(--ink-4)] font-medium uppercase">
                        {isTrialing ? 'Fin de prueba' : isActive ? 'Próximo cobro' : 'Expiración'}
                      </p>
                      {isTrialing && trialEnd ? (
                        <div className="mt-0.5">
                          <p className="text-base font-bold text-[var(--ink)]">{diasRestantes} días</p>
                          <div className="mt-1 w-full bg-gray-200 rounded-full h-1">
                            <div className="bg-blue-500 h-1 rounded-full" style={{ width: `${Math.max(5, ((14 - diasRestantes) / 14) * 100)}%` }} />
                          </div>
                        </div>
                      ) : nextBilling ? (
                        <p className={`text-base font-bold mt-0.5 ${nextBilling <= new Date() ? 'text-red-600' : 'text-[var(--ink)]'}`}>
                          {formatearFecha(nextBilling)}
                        </p>
                      ) : (
                        <p className="text-base font-bold text-[var(--ink-4)] mt-0.5">—</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Modificar plazas ── */}
                {isActive && !isInterno && (
                  <div className="ui-card p-5 p-5">
                    <h3 className="text-sm font-semibold text-[var(--ink-2)] uppercase tracking-wide mb-3">Modificar plazas</h3>

                    {(() => {
                      const newTotal = slotsTotal + slotsDelta
                      const isAdding = slotsDelta > 0
                      const isReducing = slotsDelta < 0
                      const minSlots = slotsUsados || 1
                      const canApply = slotsDelta !== 0 && newTotal >= minSlots && newTotal !== slotsTotal

                      const planPrice = billingInterval === 'yearly' ? PLAN_INFO[currentPlan]?.annualPrice : PLAN_INFO[currentPlan]?.price
                      const absDelta = Math.abs(slotsDelta)

                      return (
                        <div className="flex flex-wrap items-end gap-4">
                          <div>
                            <label className="text-[11px] text-[var(--ink-3)] block mb-1">Ajuste de plazas</label>
                            <div className="flex items-center border border-[var(--line-2)] rounded-lg overflow-hidden">
                              <button type="button"
                                onClick={() => setSlotsDelta(prev => { const n = prev - 1; return (slotsTotal + n) < minSlots ? prev : n })}
                                className="px-2.5 py-1.5 bg-[var(--bg-2)] hover:bg-[var(--bg-2)] text-[var(--ink-2)] font-bold text-sm">-</button>
                              <input type="number" value={slotsDelta}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value)
                                  if (isNaN(v)) { setSlotsDelta(0); return }
                                  if ((slotsTotal + v) < minSlots) return
                                  setSlotsDelta(v)
                                }}
                                className="w-16 text-center py-1.5 border-x border-[var(--line-2)] text-sm font-bold" />
                              <button type="button" onClick={() => setSlotsDelta(prev => prev + 1)}
                                className="px-2.5 py-1.5 bg-[var(--bg-2)] hover:bg-[var(--bg-2)] text-[var(--ink-2)] font-bold text-sm">+</button>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="text-center">
                              <p className="text-[11px] text-[var(--ink-4)]">Actual</p>
                              <p className="text-lg font-bold text-[var(--ink)]">{slotsTotal}</p>
                            </div>
                            <FiArrowRight className="h-4 w-4 text-gray-300" />
                            <div className="text-center">
                              <p className="text-[11px] text-[var(--ink-4)]">Nuevo</p>
                              <p className={`text-lg font-bold ${isAdding ? 'text-green-600' : isReducing ? 'text-orange-600' : 'text-[var(--ink)]'}`}>{newTotal}</p>
                            </div>
                          </div>

                          {isAdding && (
                            <div className="bg-green-50 rounded-lg px-3 py-2 border border-green-200">
                              <p className="text-[11px] text-green-700 font-medium">
                                {billingInterval === 'yearly'
                                  ? `+${(planPrice * absDelta * 12).toFixed(2)} €/año (cobro proporcional inmediato)`
                                  : `+${(planPrice * absDelta).toFixed(2)} €/mes (en próxima factura)`
                                }
                              </p>
                            </div>
                          )}

                          {isReducing && (
                            <div className="bg-[var(--bg-2)] rounded-lg px-3 py-2 border border-[var(--line)]">
                              <p className="text-[11px] text-[var(--ink-2)] font-medium">
                                -{(planPrice * absDelta).toFixed(2)} €/mes — efectivo en el próximo cobro
                              </p>
                            </div>
                          )}

                          <button
                            onClick={handleSlotsChange}
                            disabled={slotsLoading || !canApply}
                            className={`btn text-sm ${isAdding ? 'btn-primary' : 'btn-secondary'}`}
                          >
                            {slotsLoading ? 'Procesando...' : isAdding ? `Ampliar a ${newTotal}` : isReducing ? `Reducir a ${newTotal}` : 'Sin cambios'}
                          </button>
                        </div>
                      )
                    })()}

                    {slotsUsados > 0 && (
                      <p className="text-[10px] text-[var(--ink-4)] mt-2">
                        Mínimo {slotsUsados} plaza{slotsUsados !== 1 ? 's' : ''} (empleados activos).
                        {slotsDelta < 0 && ' La reducción se aplica en el siguiente periodo de cobro, sin generar créditos.'}
                      </p>
                    )}
                  </div>
                )}

                {/* ── Cambiar plan o modalidad ── */}
                {isActive && !isInterno && (
                  <div className="ui-card p-5 p-5">
                    <h3 className="text-sm font-semibold text-[var(--ink-2)] uppercase tracking-wide mb-3">Cambiar plan o modalidad</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Card del otro plan */}
                      {Object.entries(PLAN_INFO).filter(([key]) => key !== currentPlan).map(([planKey, plan]) => {
                        const isDowngrade = currentPlan === 'avanzado' && planKey === 'pro'
                        const isDowngradeAnual = isDowngrade && billingInterval === 'yearly'
                        return (
                          <div key={planKey} className="rounded-xl border border-[var(--line)] p-4 hover:border-[var(--line-2)] transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-bold text-[var(--ink)]">{plan.name}</h4>
                              <span className="text-lg font-extrabold text-[var(--ink)]">
                                {billingInterval === 'yearly' ? plan.annualPrice : plan.price} €
                                <span className="text-xs font-normal text-[var(--ink-4)]">/empl/mes</span>
                              </span>
                            </div>
                            <ul className="space-y-1 mb-3">
                              {plan.features.map((f, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-xs text-[var(--ink-2)]">
                                  <FiCheck className="h-3.5 w-3.5 text-green-500 shrink-0 mt-0.5" />{f}
                                </li>
                              ))}
                            </ul>
                            {isDowngrade && !isDowngradeAnual && (
                              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                                <p className="text-[11px] text-amber-700">Perderás acceso a: Ausencias, Correcciones, Geolocalización e Informes avanzados.</p>
                              </div>
                            )}
                            {isDowngradeAnual ? (
                              <div className="bg-[var(--bg-2)] border border-[var(--line)] rounded-lg p-2.5 text-center">
                                <p className="text-xs text-[var(--ink-2)] font-medium">Para cambiar de Avanzado a Pro en plan anual, contacta con nuestro equipo.</p>
                                <a href="mailto:soporte@presenzia.app" className="text-xs text-blue-600 font-bold hover:underline mt-1 inline-block">soporte@presenzia.app</a>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setChangePlanTarget({ plan: planKey, interval: billingInterval }); setShowChangePlanModal(true) }}
                                className={`w-full py-2 rounded-lg font-bold text-sm transition-colors ${
                                  isDowngrade
                                    ? 'bg-gray-200 text-[var(--ink-2)] hover:bg-gray-300'
                                    : plan.color === 'purple' ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                              >
                                {isDowngrade ? 'Cambiar a Pro (downgrade)' : `Cambiar a ${plan.name}`}
                              </button>
                            )}
                          </div>
                        )
                      })}

                      {/* Cambio de modalidad */}
                      {billingInterval === 'monthly' ? (
                        <div className="rounded-xl border border-green-200 bg-green-50/40 p-4">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-bold text-[var(--ink)] flex items-center gap-1.5"><FiCalendar className="h-4 w-4 text-green-600" />Pasa a anual</h4>
                            <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">-17%</span>
                          </div>
                          <p className="text-xs text-[var(--ink-3)] mb-2">Paga 10 meses, disfruta 12. Ahorras {((PLAN_INFO[currentPlan]?.price - PLAN_INFO[currentPlan]?.annualPrice) * 12).toFixed(2)} €/empl/año.</p>
                          <div className="flex justify-between text-xs mb-3 bg-white rounded-lg p-2 border border-green-100">
                            <span className="text-[var(--ink-3)]">Actual: {PLAN_INFO[currentPlan]?.price} €</span>
                            <span className="text-green-700 font-bold">Anual: {PLAN_INFO[currentPlan]?.annualPrice} €/empl/mes</span>
                          </div>
                          <button
                            onClick={() => { setChangePlanTarget({ plan: currentPlan, interval: 'yearly' }); setShowChangePlanModal(true) }}
                            className="w-full py-2 rounded-lg font-bold text-sm bg-green-600 text-white hover:bg-green-700 transition-colors"
                          >
                            Cambiar a anual
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-[var(--line)] bg-[var(--bg-2)] p-4 flex items-start gap-3">
                          <FiInfo className="h-5 w-5 text-[var(--ink-4)] shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-[var(--ink-2)] text-sm">Facturación anual activa</h4>
                            <p className="text-xs text-[var(--ink-3)] mt-0.5">El cambio a mensual se aplicará al finalizar el periodo anual en curso.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Activar plan (trial / expirado) ── */}
                {!isActive && !isInterno && (
                  <div className="ui-card p-5 p-5">
                    <h3 className="text-sm font-semibold text-[var(--ink-2)] uppercase tracking-wide mb-3">
                      {isTrialing ? 'Elige tu plan' : 'Activa tu plan'}
                    </h3>

                    <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-[var(--bg-2)] rounded-lg">
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-[var(--ink-3)]"><FiUsers className="inline h-3.5 w-3.5 mr-0.5" />Plazas:</label>
                        <div className="flex items-center border border-[var(--line-2)] rounded-lg overflow-hidden">
                          <button type="button" onClick={() => setSelectedSlots(Math.max(1, selectedSlots - 1))}
                            className="px-2.5 py-1 bg-[var(--bg-2)] hover:bg-[var(--bg-2)] text-[var(--ink-2)] font-bold text-sm">-</button>
                          <input type="number" min="1" max="999" value={selectedSlots}
                            onChange={(e) => setSelectedSlots(Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-14 text-center py-1 border-x border-[var(--line-2)] text-sm font-bold" />
                          <button type="button" onClick={() => setSelectedSlots(selectedSlots + 1)}
                            className="px-2.5 py-1 bg-[var(--bg-2)] hover:bg-[var(--bg-2)] text-[var(--ink-2)] font-bold text-sm">+</button>
                        </div>
                      </div>
                      <div className="flex rounded-lg overflow-hidden border border-[var(--line-2)]">
                        <button type="button" onClick={() => setSelectedInterval('monthly')}
                          className={`px-3 py-1 text-xs font-medium transition-colors ${selectedInterval === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-[var(--ink-2)] hover:bg-[var(--bg-2)]'}`}>
                          Mensual
                        </button>
                        <button type="button" onClick={() => setSelectedInterval('yearly')}
                          className={`px-3 py-1 text-xs font-medium transition-colors ${selectedInterval === 'yearly' ? 'bg-blue-600 text-white' : 'bg-white text-[var(--ink-2)] hover:bg-[var(--bg-2)]'}`}>
                          Anual (-17%)
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(PLAN_INFO).map(([planKey, plan]) => {
                        const price = selectedInterval === 'yearly' ? plan.annualPrice : plan.price
                        const total = (price * selectedSlots * (selectedInterval === 'yearly' ? 12 : 1)).toFixed(2)
                        const intervalLabel = selectedInterval === 'yearly' ? 'año' : 'mes'
                        const isCurrent = currentPlan === planKey

                        return (
                          <div key={planKey} className={`relative rounded-xl border-2 p-4 transition-all ${
                            isCurrent
                              ? plan.color === 'purple' ? 'border-purple-300 bg-purple-50/30' : 'border-blue-300 bg-blue-50/30'
                              : 'border-[var(--line)]'
                          }`}>
                            {isCurrent && (
                              <span className="absolute -top-2 left-3 px-2 py-0.5 bg-blue-600 text-white text-[9px] font-bold rounded-full uppercase">Recomendado</span>
                            )}
                            <div className="flex items-baseline justify-between">
                              <h4 className="font-bold text-[var(--ink)]">{plan.name}</h4>
                              <div>
                                <span className="text-2xl font-extrabold text-[var(--ink)]">{plan.price}€</span>
                                <span className="text-xs text-[var(--ink-4)]">/empl/mes</span>
                              </div>
                            </div>
                            <p className="text-[10px] text-[var(--ink-4)] text-right">{plan.annualPrice}€/empl/mes en anual</p>
                            <ul className="space-y-1 my-3">
                              {plan.features.map((f, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-xs text-[var(--ink-2)]">
                                  <FiCheck className="h-3.5 w-3.5 text-green-500 shrink-0 mt-0.5" />{f}
                                </li>
                              ))}
                            </ul>
                            <div className="text-center mb-2 p-1.5 bg-white/80 rounded-lg border border-[var(--line)]">
                              <p className="text-[11px] text-[var(--ink-3)]">{selectedSlots} plaza{selectedSlots > 1 ? 's' : ''} × {price}€</p>
                              <p className="text-base font-bold text-[var(--ink)]">{total}€<span className="text-xs font-normal text-[var(--ink-4)]">/{intervalLabel}</span></p>
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  const res = await billingAPI.createCheckout({ plan: planKey, interval: selectedInterval, slots: selectedSlots })
                                  if (res.data.url) window.location.href = res.data.url
                                } catch (err) { toast.error(err.response?.data?.error || 'Error al iniciar el pago') }
                              }}
                              className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg font-bold text-sm transition-colors ${
                                plan.color === 'purple' ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              Activar {plan.name} <FiArrowRight className="h-4 w-4" />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* ── Portal y soporte ── */}
                {(isActive || (empleado?.rol === 'admin' && soporteUrl)) && (
                  <div className="flex flex-wrap gap-2">
                    {isActive && !isInterno && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await billingAPI.createPortal()
                            if (res.data.url) window.location.href = res.data.url
                          } catch (err) { toast.error('Error al abrir el portal de facturación') }
                        }}
                        className="text-xs font-medium text-[var(--ink-3)] hover:text-blue-600 flex items-center gap-1 transition-colors"
                      >
                        <FiExternalLink className="h-3.5 w-3.5" /> Ver facturas y método de pago
                      </button>
                    )}
                    {isInterno && (
                      <p className="text-xs text-indigo-500">Cuenta gestionada internamente — contacta con el administrador para cambios en tu plan.</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )
      })()}

      {/* ════════════════════════════════════════════════ */}
      {/* Modal: Confirmar cambio de plan                 */}
      {/* ════════════════════════════════════════════════ */}
      {showChangePlanModal && changePlanTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => { setShowChangePlanModal(false); setChangePlanTarget(null) }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-[var(--ink)] mb-3">Confirmar cambio</h3>

            <div className="bg-[var(--bg-2)] rounded-lg p-3 mb-3 space-y-1.5 text-sm">
              {changePlanTarget.plan !== currentPlan && (
                <div className="flex justify-between">
                  <span className="text-[var(--ink-3)]">Plan</span>
                  <span className="font-medium">{PLAN_INFO[currentPlan]?.name} <FiArrowRight className="inline h-3 w-3 mx-0.5" /> {PLAN_INFO[changePlanTarget.plan]?.name}</span>
                </div>
              )}
              {changePlanTarget.interval !== billingInterval && (
                <div className="flex justify-between">
                  <span className="text-[var(--ink-3)]">Facturación</span>
                  <span className="font-medium">{billingInterval === 'yearly' ? 'Anual' : 'Mensual'} <FiArrowRight className="inline h-3 w-3 mx-0.5" /> {changePlanTarget.interval === 'yearly' ? 'Anual' : 'Mensual'}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-[var(--ink-3)]">Plazas</span>
                <span className="font-medium">{billingStatus?.slotsTotal || 0} (sin cambios)</span>
              </div>
            </div>

            <p className="text-xs text-blue-600 bg-blue-50 rounded-lg p-2 mb-4">
              <FiInfo className="inline mr-1" />
              {billingInterval === 'yearly' || changePlanTarget.interval === 'yearly'
                ? 'Se realizará un cobro inmediato con el ajuste proporcional.'
                : 'El ajuste se aplicará en la próxima factura.'
              }
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => { setShowChangePlanModal(false); setChangePlanTarget(null) }}
                className="flex-1 py-2 rounded-lg font-bold text-sm border border-[var(--line-2)] text-[var(--ink-2)] hover:bg-[var(--bg-2)]"
                disabled={changingPlan}
              >
                Cancelar
              </button>
              <button
                onClick={handleChangePlan}
                disabled={changingPlan}
                className="flex-1 py-2 rounded-lg font-bold text-sm bg-blue-600 text-white hover:bg-blue-700"
              >
                {changingPlan ? 'Procesando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConfiguracionEmpresa
